"use client";

import { CircleHelp } from "lucide-react";
import type { EventPageData } from "@/actions/event";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { buttonVariants } from "@/components/ui/button";
import { captureIdentifiedClientEvent } from "@/lib/analytics/client";
import type { EventEntryAttribution } from "@/lib/analytics/entry-point";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { cn } from "@/lib/utils";
import type { HiiveSummary } from "./event-page.shared";

interface EventFooterCtaProps {
  priceSummaryText: string;
  secondarySummaryText?: string | null;
  canBookEvent: boolean;
  bookingUrl?: string | null;
  selectedOccurrenceId?: number;
  event: EventPageData["event"];
  hiive: HiiveSummary;
  isCtaDisabled: boolean;
  ctaLabel: string;
  onCtaClick?: () => void;
  /**
   * Fired when the CTA navigates rather than opening something in place.
   *
   * `captureBookTicketsClicked` deliberately no-ops unless the viewer can
   * actually book, so a tier-walled CTA — which is a Link, and is the most
   * interesting click on the page for a members-only event — emitted nothing.
   */
  onCtaNavigate?: () => void;
  onInfoClick?: () => void;
  infoLabel?: string;
  /**
   * Signed-in viewer, if any. Without it the click was captured anonymously and
   * could not be joined to the rest of the funnel — the one stage that proves
   * somebody came through the event page rather than a direct link.
   */
  viewerUserId?: string | null;
  entryAttribution?: EventEntryAttribution | null;
  /**
   * `floating` is the phone's bar pinned over the bottom of the screen;
   * `inline` is the desktop card under the hero, in the sticky media column.
   */
  variant?: "floating" | "inline";
}

export const EventFooterCta = ({
  priceSummaryText,
  secondarySummaryText,
  canBookEvent,
  bookingUrl,
  selectedOccurrenceId,
  event,
  hiive,
  isCtaDisabled,
  ctaLabel,
  onCtaClick,
  onCtaNavigate,
  onInfoClick,
  infoLabel = "More information",
  viewerUserId,
  entryAttribution,
  variant = "floating",
}: EventFooterCtaProps) => {
  const shouldRender = Boolean(bookingUrl) || Boolean(priceSummaryText);

  // One definition for both branches below. They had drifted into two copies of
  // the same payload, which is how one of them would eventually stop matching.
  const captureBookTicketsClicked = () => {
    if (!canBookEvent) return;
    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.EVENT_BOOK_TICKETS_CLICKED,
      viewerUserId,
      {
        event_id: event.id,
        event_name: event.name,
        event_slug: event.slug,
        hiive_id: hiive.id,
        hiive_slug: hiive.slug,
        selected_occurrence_id: selectedOccurrenceId,
        price_summary: priceSummaryText,
        ...entryAttribution,
      },
    );
  };
  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        variant === "floating"
          ? // `wide:hidden`: the server renders this before it knows the width;
            // a desktop mounts the inline card instead once it does. Pinned
            // between the side safe areas, not the screen: iPhone Duo's outer
            // display puts the status bar on a side edge.
            "fixed right-[env(safe-area-inset-right,0px)] bottom-[max(env(safe-area-inset-bottom,0px),1rem)] left-[env(safe-area-inset-left,0px)] z-1000 mx-auto max-w-2xl px-3 wide:hidden"
          : "w-full",
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-[22px] border border-white/70 bg-white/65 px-3 py-2 backdrop-blur-2xl backdrop-saturate-150 dark:border-white/[0.14] dark:bg-[#101319]/70",
          variant === "floating"
            ? "shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_18px_42px_rgba(15,23,42,0.18)] dark:shadow-2xl"
            : "p-3 shadow-sm",
        )}
      >
        {/* Wraps the actions under the price when the card is narrow: the
            inline card sits under the media column, which a short landscape
            screen (iPhone Duo closed and rotated) caps at ~180pt. */}
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-28 flex-1">
            <p className="text-sm leading-tight font-semibold break-words text-text-primary sm:text-base dark:text-white/90">
              {priceSummaryText}
            </p>
            {secondarySummaryText ? (
              <p className="mt-0.5 text-xs leading-tight break-words text-text-secondary dark:text-white/55">
                {secondarySummaryText}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {onInfoClick ? (
              <button
                type="button"
                aria-label={infoLabel}
                onClick={onInfoClick}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-secondary transition-colors hover:bg-background-alt hover:text-text-primary focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:outline-none dark:bg-glass-bg-10 dark:text-white/82 dark:hover:bg-white/16 dark:hover:text-white"
              >
                <CircleHelp className="h-5 w-5" />
              </button>
            ) : null}

            {/*
              Three branches, and the third is deliberately nothing.

              A closed or otherwise unbookable event used to render a grey pill
              reading "Booking unavailable": a control shaped exactly like the
              one that sells a ticket, which does nothing when tapped. The
              caption to the left already carries the whole message — and on a
              closed event that caption is the organiser's own words — so a
              button with no destination is not a fallback, it is a dead end
              wearing a button's clothes.
            */}
            {onCtaClick && !isCtaDisabled ? (
              <button
                type="button"
                onClick={() => {
                  onCtaClick();
                  captureBookTicketsClicked();
                }}
                className={buttonVariants({
                  variant: "solid",
                  className:
                    "h-auto px-5 py-3 text-center font-semibold sm:px-6 sm:text-base",
                })}
              >
                {ctaLabel}
              </button>
            ) : bookingUrl && !isCtaDisabled ? (
              <BackAwareLink
                href={bookingUrl}
                onClick={() => {
                  captureBookTicketsClicked();
                  onCtaNavigate?.();
                }}
                className={cn(
                  buttonVariants({
                    variant: "solid",
                    className:
                      "h-auto px-5 py-3 text-center font-semibold sm:px-6 sm:text-base",
                  }),
                  isCtaDisabled &&
                    "pointer-events-none cursor-not-allowed opacity-50",
                )}
              >
                {ctaLabel}
              </BackAwareLink>
            ) : ctaLabel === "Sold out" ? (
              <button
                type="button"
                disabled
                className="shrink-0 rounded-full bg-surface-alt px-5 py-3 text-sm font-semibold text-text-secondary sm:px-6 sm:text-base"
              >
                Sold out
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
